import { useMemo, useState, useEffect, useCallback } from 'react';
import { useFormacao } from '@/context/FormacaoContext';
import { useFuncionariosFormacao } from '@/context/FuncionariosFormacaoContext';
import { supabase } from '@/lib/supabase';

interface Unidade {
  id: string;
  nome: string;
  sigla: string | null;
}

interface FuncionarioComHoras {
  id: string;
  nome: string;
  carreira: string;
  unidade: string;
  horasPorAno: Record<string, number>;
  totalHoras: number;
  formacoes: number;
}

interface UnidadeMapEntry {
  funcionarios: Record<string, FuncionarioComHoras>;
  chefes: string[];
}

interface UnidadeRow {
  unidadeId: string;
  unidadeNome: string;
  sigla: string;
  chefe: string | null;
  funcionarios: FuncionarioComHoras[];
  totalHoras: number;
  totalFormacoes: number;
  horasPorAno: Record<string, number>;
}

export default function RelatorioPorUnidadeFormacao() {
  const { formacoes, participantes, loading } = useFormacao();
  const { funcionarios } = useFuncionariosFormacao();

  const [unidades, setUnidades] = useState<Unidade[]>([]);
  const [loadingUnidades, setLoadingUnidades] = useState(true);
  const [anoSelecionado, setAnoSelecionado] = useState<string>('todos');
  const [expandidas, setExpandidas] = useState<Set<string>>(new Set());
  const [chefeSelecionado, setChefeSelecionado] = useState<string>('todos');
  const [search, setSearch] = useState('');

  // Carregar unidades orgânicas da BD
  useEffect(() => {
    async function loadUnidades() {
      setLoadingUnidades(true);
      const { data } = await supabase.from('unidades_organicas').select('id, nome, sigla').eq('ativo', true).order('nome');
      setUnidades(data ?? []);
      setLoadingUnidades(false);
    }
    loadUnidades();
  }, []);

  const anosDisponiveis = useMemo(() => {
    const anos = new Set<string>();
    formacoes.forEach((f) => {
      const a = (f.data_inicio ?? f.data_fim ?? '').slice(0, 4);
      if (a) anos.add(a);
    });
    const lista = Array.from(anos).sort((a, b) => b.localeCompare(a));
    if (lista.length === 0) lista.push('2025');
    return lista;
  }, [formacoes]);

  // Mapa de unidades por nome (lowercase) para lookup rápido
  const unidadesPorNome = useMemo(() => {
    const map: Record<string, Unidade> = {};
    unidades.forEach((u) => {
      map[u.nome.trim().toLowerCase()] = u;
    });
    return map;
  }, [unidades]);

  const dadosPorUnidade = useMemo((): UnidadeRow[] => {
    // Formações por ano
    const formacoesFiltradas = anoSelecionado === 'todos'
      ? formacoes
      : formacoes.filter((f) => (f.data_inicio ?? f.data_fim ?? '').startsWith(anoSelecionado));

    const idsFormacoes = new Set(formacoesFiltradas.map((f) => f.id));
    const participantesFiltrados = participantes.filter((p) => idsFormacoes.has(p.formacao_id));

    // Mapa: unidade -> { funcionarios, chefes }
    const unidadeMap: Record<string, UnidadeMapEntry> = {};

    participantesFiltrados.forEach((p) => {
      const func = funcionarios.find((fu) => fu.id === p.funcionario_id);
      const formacao = formacoesFiltradas.find((f) => f.id === p.formacao_id);
      if (!func || !formacao) return;

      const rawUnidade = func.unidadeOrganica?.trim() || '';
      const unidadeNome = rawUnidade || 'Sem Unidade';
      // Procurar unidade na BD por nome (case-insensitive)
      const u = unidadesPorNome[unidadeNome.toLowerCase()];
      const unidadeId = u?.id ?? (rawUnidade || 'sem_unidade');
      const sigla = u?.sigla ?? '';
      const chefe = func.superiorHierarquico ?? null;

      const ano = (formacao.data_inicio ?? formacao.data_fim ?? '').slice(0, 4) || '—';
      const horas = Number(formacao.duracao_horas) || 0;

      if (!unidadeMap[unidadeId]) {
        unidadeMap[unidadeId] = { funcionarios: {}, chefes: [] };
      }

      if (chefe) unidadeMap[unidadeId].chefes.push(chefe);

      if (!unidadeMap[unidadeId].funcionarios[func.id]) {
        unidadeMap[unidadeId].funcionarios[func.id] = {
          id: func.id,
          nome: func.nome,
          carreira: func.carreira ?? '—',
          unidade: unidadeNome,
          horasPorAno: {},
          totalHoras: 0,
          formacoes: 0,
        };
      }

      const fch = unidadeMap[unidadeId].funcionarios[func.id];
      if (!fch.horasPorAno[ano]) fch.horasPorAno[ano] = 0;
      fch.horasPorAno[ano] += horas;
      fch.totalHoras += horas;
      fch.formacoes += 1;
    });

    // Construir linhas
    let rows: UnidadeRow[] = [];

    // Unidades com dados
    Object.entries(unidadeMap).forEach(([unidadeId, data]) => {
      const funcMap = data.funcionarios;
      const funcs = Object.values(funcMap).sort((a, b) => b.totalHoras - a.totalHoras);
      const totalHoras = funcs.reduce((s, f) => s + f.totalHoras, 0);
      const totalFormacoes = funcs.reduce((s, f) => s + f.formacoes, 0);

      // Determinar chefe mais frequente
      const chefes = data.chefes.filter(Boolean);
      const freq: Record<string, number> = {};
      chefes.forEach((c) => { freq[c] = (freq[c] ?? 0) + 1; });
      let chefe: string | null = null;
      let maxCount = 0;
      Object.entries(freq).forEach(([nome, count]) => {
        if (count > maxCount) {
          maxCount = count;
          chefe = nome;
        }
      });

      // Horas por ano para a unidade
      const horasPorAno: Record<string, number> = {};
      funcs.forEach((f) => {
        Object.entries(f.horasPorAno).forEach(([ano, h]) => {
          if (!horasPorAno[ano]) horasPorAno[ano] = 0;
          horasPorAno[ano] += h;
        });
      });

      const u = unidades.find((un) => un.id === unidadeId);
      rows.push({
        unidadeId,
        unidadeNome: u?.nome ?? (funcs[0]?.unidade ?? unidadeId),
        sigla: u?.sigla ?? '',
        chefe,
        funcionarios: funcs,
        totalHoras,
        totalFormacoes,
        horasPorAno,
      });
    });

    // Unidades sem formandos (com 0)
    const unidadesComDados = new Set(rows.map((r) => r.unidadeId));
    unidades.forEach((u) => {
      if (!unidadesComDados.has(u.id)) {
        rows.push({
          unidadeId: u.id,
          unidadeNome: u.nome,
          sigla: u.sigla ?? '',
          chefe: null,
          funcionarios: [],
          totalHoras: 0,
          totalFormacoes: 0,
          horasPorAno: {},
        });
      }
    });

    // Ordenar: primeiro as com dados (por total horas), depois as vazias
    rows.sort((a, b) => {
      if (a.totalHoras === 0 && b.totalHoras === 0) return a.unidadeNome.localeCompare(b.unidadeNome);
      if (a.totalHoras === 0) return 1;
      if (b.totalHoras === 0) return -1;
      return b.totalHoras - a.totalHoras;
    });

    // Filtrar por pesquisa
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((r) =>
        r.unidadeNome.toLowerCase().includes(q) ||
        r.sigla?.toLowerCase().includes(q) ||
        r.chefe?.toLowerCase().includes(q) ||
        r.funcionarios.some((f) =>
          f.nome.toLowerCase().includes(q) || f.carreira.toLowerCase().includes(q)
        )
      );
    }

    // Filtrar por chefe de unidade
    if (chefeSelecionado !== 'todos') {
      rows = rows.filter((r) => r.chefe === chefeSelecionado);
    }

    return rows;
  }, [formacoes, participantes, funcionarios, unidades, unidadesPorNome, anoSelecionado, search, chefeSelecionado]);

  const totais = useMemo(() => {
    return dadosPorUnidade.reduce(
      (acc, r) => ({
        funcionarios: acc.funcionarios + r.funcionarios.length,
        horas: acc.horas + r.totalHoras,
        formacoes: acc.formacoes + r.totalFormacoes,
      }),
      { funcionarios: 0, horas: 0, formacoes: 0 }
    );
  }, [dadosPorUnidade]);

  // Lista de chefes únicos para o filtro
  const listaChefes = useMemo(() => {
    const chefes = new Set<string>();
    dadosPorUnidade.forEach((r) => {
      if (r.chefe) chefes.add(r.chefe);
    });
    return Array.from(chefes).sort((a, b) => a.localeCompare(b));
  }, [dadosPorUnidade]);

  const toggleUnidade = useCallback((id: string) => {
    setExpandidas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandirTodas = useCallback(() => {
    setExpandidas(new Set(dadosPorUnidade.filter((r) => r.funcionarios.length > 0).map((r) => r.unidadeId)));
  }, [dadosPorUnidade]);

  const recolherTodas = useCallback(() => {
    setExpandidas(new Set());
  }, []);

  if (loading || loadingUnidades) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line text-2xl animate-spin text-zinc-300" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Filtros */}
      <div className="bg-white rounded-xl border border-zinc-100 p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-zinc-100 text-zinc-600">
            <i className="ri-calendar-line text-sm" />
          </div>
          <span className="text-[12px] font-semibold text-zinc-600">Ano</span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setAnoSelecionado('todos')}
            className={`whitespace-nowrap px-4 py-2 text-[13px] font-semibold rounded-lg border-2 transition-all cursor-pointer ${
              anoSelecionado === 'todos'
                ? 'border-zinc-900 bg-zinc-900 text-white'
                : 'border-zinc-100 text-zinc-500 bg-white hover:border-zinc-300'
            }`}
          >
            Todos
          </button>
          {anosDisponiveis.map((a) => (
            <button
              key={a}
              onClick={() => setAnoSelecionado(a)}
              className={`whitespace-nowrap px-4 py-2 text-[13px] font-semibold rounded-lg border-2 transition-all cursor-pointer ${
                anoSelecionado === a
                  ? 'border-zinc-900 bg-zinc-900 text-white'
                  : 'border-zinc-100 text-zinc-500 bg-white hover:border-zinc-300'
              }`}
            >
              {a}
            </button>
          ))}
        </div>

        <div className="h-6 w-px bg-zinc-200 mx-1" />

        {/* Filtro por Chefe */}
        {listaChefes.length > 0 && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-zinc-100 text-zinc-600">
              <i className="ri-user-star-line text-sm" />
            </div>
            <span className="text-[12px] font-semibold text-zinc-600">Chefe</span>
            <select
              value={chefeSelecionado}
              onChange={(e) => setChefeSelecionado(e.target.value)}
              className="text-[13px] border border-zinc-200 rounded-lg px-3 py-2 bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300 cursor-pointer"
            >
              <option value="todos">Todos os chefes</option>
              {listaChefes.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        )}

        <div className="h-6 w-px bg-zinc-200 mx-1" />

        <div className="relative flex-1 min-w-[200px]">
          <div className="w-8 h-8 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">
            <i className="ri-search-line text-sm" />
          </div>
          <input
            type="text"
            placeholder="Pesquisar unidade, formando ou carreira..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300"
          />
        </div>

        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={expandirTodas}
            className="whitespace-nowrap text-[12px] font-medium text-zinc-500 hover:text-zinc-800 px-3 py-2 rounded-lg hover:bg-zinc-100 transition-colors cursor-pointer"
          >
            Expandir tudo
          </button>
          <button
            onClick={recolherTodas}
            className="whitespace-nowrap text-[12px] font-medium text-zinc-500 hover:text-zinc-800 px-3 py-2 rounded-lg hover:bg-zinc-100 transition-colors cursor-pointer"
          >
            Recolher tudo
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { icon: 'ri-building-2-line', value: String(dadosPorUnidade.filter((r) => r.totalHoras > 0).length), label: 'Unidades com Formação' },
          { icon: 'ri-group-line', value: String(totais.funcionarios), label: 'Formandos' },
          { icon: 'ri-time-line', value: `${totais.horas}h`, label: 'Horas Totais' },
          { icon: 'ri-book-open-line', value: String(totais.formacoes), label: 'Participações' },
        ].map((kpi) => (
          <div key={kpi.label} className="bg-white rounded-xl border border-zinc-100 px-4 py-4 text-center">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-zinc-100 mx-auto mb-2">
              <i className={`${kpi.icon} text-sm text-zinc-500`} />
            </div>
            <p className="text-[22px] font-black text-zinc-900 leading-none">{kpi.value}</p>
            <p className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest mt-1.5">{kpi.label}</p>
          </div>
        ))}
      </div>

      {/* Tabela por Unidade */}
      <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-1 h-5 bg-zinc-900 rounded-full" />
            <h3 className="text-[14px] font-bold text-zinc-800">Formação por Unidade Operativa</h3>
          </div>
          <p className="text-[11px] text-zinc-400">
            {dadosPorUnidade.filter((r) => r.totalHoras > 0).length} unidade(s) com dados
          </p>
        </div>

        <table className="w-full">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <th className="px-6 py-3 text-left text-[10px] font-bold text-zinc-400 uppercase tracking-wider w-[280px]">Unidade Operativa</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold text-zinc-400 uppercase tracking-wider w-[200px]">Chefe de Unidade</th>
              <th className="px-4 py-3 text-center text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Formandos</th>
              <th className="px-4 py-3 text-center text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Participações</th>
              <th className="px-4 py-3 text-center text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Horas</th>
              {anoSelecionado === 'todos' && anosDisponiveis.map((a) => (
                <th key={a} className="px-4 py-3 text-center text-[10px] font-bold text-zinc-400 uppercase tracking-wider">{a}</th>
              ))}
              <th className="px-4 py-3 text-center text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Média/Formando</th>
            </tr>
          </thead>
          <tbody>
            {dadosPorUnidade.length === 0 ? (
              <tr>
                <td colSpan={anoSelecionado === 'todos' ? 6 + anosDisponiveis.length : 7} className="px-6 py-12 text-center text-[13px] text-zinc-400">
                  <div className="flex flex-col items-center gap-2">
                    <i className="ri-search-line text-3xl text-zinc-200" />
                    <p>Nenhuma unidade encontrada</p>
                  </div>
                </td>
              </tr>
            ) : (
              dadosPorUnidade.map((row) => {
                const mediaHoras = row.funcionarios.length > 0
                  ? Math.round(row.totalHoras / row.funcionarios.length)
                  : 0;
                return (
                  <>
                    {/* Linha principal da unidade */}
                    <tr
                      key={`u-${row.unidadeId}`}
                      onClick={() => row.funcionarios.length > 0 && toggleUnidade(row.unidadeId)}
                      className={`border-b border-zinc-100 ${
                        row.funcionarios.length > 0 ? 'bg-zinc-50/40 hover:bg-zinc-50 cursor-pointer' : 'bg-white'
                      } transition-colors`}
                    >
                      <td className="px-6 py-3.5">
                        <div className="flex items-center gap-2">
                          {row.funcionarios.length > 0 && (
                            <div className="w-4 h-4 flex items-center justify-center text-zinc-400 flex-shrink-0">
                              {expandidas.has(row.unidadeId) ? (
                                <i className="ri-arrow-down-s-line text-sm" />
                              ) : (
                                <i className="ri-arrow-right-s-line text-sm" />
                              )}
                            </div>
                          )}
                          <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-100 flex-shrink-0">
                            <span className="text-[10px] font-black text-zinc-500">
                              {row.sigla || row.unidadeNome.slice(0, 2).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <span className="text-[13px] font-bold text-zinc-800">{row.unidadeNome}</span>
                            {row.sigla && <span className="text-[10px] text-zinc-400 ml-1.5">({row.sigla})</span>}
                            {row.chefe && (
                              <div className="flex items-center gap-1 mt-0.5">
                                <div className="w-3 h-3 flex items-center justify-center">
                                  <i className="ri-user-star-fill text-[9px] text-amber-500" />
                                </div>
                                <span className="text-[10px] text-zinc-500">{row.chefe}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        {row.chefe ? (
                          <div className="flex items-center gap-1.5">
                            <div className="w-6 h-6 flex items-center justify-center rounded-full bg-amber-100 flex-shrink-0">
                              <span className="text-[9px] font-bold text-amber-700">{row.chefe.charAt(0)}</span>
                            </div>
                            <span className="text-[12px] font-semibold text-zinc-700">{row.chefe}</span>
                          </div>
                        ) : (
                          <span className="text-[12px] text-zinc-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className={`inline-flex items-center justify-center min-w-[32px] h-7 px-2.5 rounded-full text-[12px] font-bold ${
                          row.funcionarios.length === 0 ? 'bg-zinc-50 text-zinc-300' : 'bg-violet-100 text-violet-700'
                        }`}>
                          {row.funcionarios.length}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className={`inline-flex items-center justify-center min-w-[32px] h-7 px-2.5 rounded-full text-[12px] font-bold ${
                          row.totalFormacoes === 0 ? 'bg-zinc-50 text-zinc-300' : 'bg-sky-100 text-sky-700'
                        }`}>
                          {row.totalFormacoes}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className={`inline-flex items-center justify-center min-w-[40px] h-7 px-2.5 rounded-full text-[12px] font-bold ${
                          row.totalHoras === 0 ? 'bg-zinc-50 text-zinc-300' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {row.totalHoras === 0 ? '0h' : `${row.totalHoras}h`}
                        </span>
                      </td>
                      {anoSelecionado === 'todos' && anosDisponiveis.map((a) => (
                        <td key={a} className="px-4 py-3.5 text-center">
                          <span className={`text-[12px] font-semibold ${
                            (row.horasPorAno[a] ?? 0) === 0 ? 'text-zinc-300' : 'text-zinc-700'
                          }`}>
                            {row.horasPorAno[a] ?? 0}h
                          </span>
                        </td>
                      ))}
                      <td className="px-4 py-3.5 text-center">
                        <span className={`text-[12px] font-semibold ${
                          mediaHoras === 0 ? 'text-zinc-300' : 'text-zinc-600'
                        }`}>
                          {mediaHoras}h
                        </span>
                      </td>
                    </tr>

                    {/* Sub-linhas: funcionários da unidade */}
                    {expandidas.has(row.unidadeId) && row.funcionarios.length > 0 && (
                      <>
                        {/* Cabeçalho sub-tabela */}
                        <tr className="bg-zinc-50/60">
                          <td className="pl-14 pr-4 py-2" />
                          <td colSpan={anoSelecionado === 'todos' ? 6 + anosDisponiveis.length : 6} className="px-0 py-0">
                            <table className="w-full">
                              <thead>
                                <tr className="border-b border-zinc-100">
                                  <th className="pl-4 pr-3 py-2 text-left text-[9px] font-bold text-zinc-400 uppercase tracking-wider w-[200px]">Formando</th>
                                  <th className="px-3 py-2 text-left text-[9px] font-bold text-zinc-400 uppercase tracking-wider">Carreira</th>
                                  <th className="px-3 py-2 text-center text-[9px] font-bold text-zinc-400 uppercase tracking-wider">Formações</th>
                                  <th className="px-3 py-2 text-center text-[9px] font-bold text-zinc-400 uppercase tracking-wider">Horas</th>
                                  {anoSelecionado === 'todos' && anosDisponiveis.map((a) => (
                                    <th key={a} className="px-3 py-2 text-center text-[9px] font-bold text-zinc-400 uppercase tracking-wider">{a}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {row.funcionarios.map((f) => (
                                  <tr key={f.id} className="border-b border-zinc-50">
                                    <td className="pl-4 pr-3 py-2">
                                      <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 flex items-center justify-center rounded-full bg-zinc-100 flex-shrink-0">
                                          <span className="text-[9px] font-bold text-zinc-500">{f.nome.charAt(0)}</span>
                                        </div>
                                        <span className="text-[12px] font-semibold text-zinc-700">{f.nome}</span>
                                      </div>
                                    </td>
                                    <td className="px-3 py-2">
                                      <span className="text-[11px] text-zinc-500">{f.carreira}</span>
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <span className="text-[11px] font-semibold text-zinc-600">{f.formacoes}</span>
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <span className="text-[11px] font-bold text-zinc-700">{f.totalHoras}h</span>
                                    </td>
                                    {anoSelecionado === 'todos' && anosDisponiveis.map((a) => (
                                      <td key={a} className="px-3 py-2 text-center">
                                        <span className={`text-[11px] font-semibold ${
                                          (f.horasPorAno[a] ?? 0) === 0 ? 'text-zinc-300' : 'text-zinc-600'
                                        }`}>
                                          {f.horasPorAno[a] ?? 0}h
                                        </span>
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      </>
                    )}
                  </>
                );
              })
            )}

            {/* TOTAL GERAL */}
            {dadosPorUnidade.filter((r) => r.totalHoras > 0).length > 0 && (
              <tr className="bg-zinc-900 border-t-2 border-zinc-900">
                <td className="px-6 py-4">
                  <span className="text-[13px] font-black text-white tracking-wide">TOTAL GERAL</span>
                </td>
                <td className="px-4 py-4" />
                <td className="px-4 py-4 text-center">
                  <span className="inline-flex items-center justify-center min-w-[32px] h-7 px-2.5 rounded-full bg-white/20 text-white text-[12px] font-black">
                    {totais.funcionarios}
                  </span>
                </td>
                <td className="px-4 py-4 text-center">
                  <span className="inline-flex items-center justify-center min-w-[32px] h-7 px-2.5 rounded-full bg-white/20 text-white text-[12px] font-black">
                    {totais.formacoes}
                  </span>
                </td>
                <td className="px-4 py-4 text-center">
                  <span className="inline-flex items-center justify-center min-w-[40px] h-7 px-2.5 rounded-full bg-white/20 text-white text-[12px] font-black">
                    {totais.horas}h
                  </span>
                </td>
                {anoSelecionado === 'todos' && anosDisponiveis.map((a) => {
                  const horasAno = dadosPorUnidade.reduce((s, r) => s + (r.horasPorAno[a] ?? 0), 0);
                  return (
                    <td key={a} className="px-4 py-4 text-center">
                      <span className="text-[12px] font-black text-white">{horasAno}h</span>
                    </td>
                  );
                })}
                <td className="px-4 py-4 text-center">
                  <span className="text-[12px] font-black text-white">
                    {totais.funcionarios > 0 ? Math.round(totais.horas / totais.funcionarios) : 0}h
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-zinc-400 text-center">
        Horas = soma das durações das formações em que cada funcionário participou. Clique numa unidade para ver os formandos detalhados.
      </p>
    </div>
  );
}